import type { Supplier } from '../interfaces/ILogistics.js';
import { UnauthorizedError } from './errors.js';
import {
  apiFailure,
  LogisticsApiError,
  LogisticsNotFoundError,
  readLogisticsFailure,
  type ILogisticsFailure,
} from './logisticsErrors.js';
import type { AuthedFetch } from './transport.js';

/** A supplier draft as an order write carries it (`newSupplier`); as
 *  `supplierEdit` every field is optional. The two contact fields are
 *  nullable, and null clears them. */
export type ISupplierBody = {
  name: string;
  contactName?: string | null;
  phone?: string | null;
};

export type ISupplierPatch = Partial<ISupplierBody>;

/** An order write's new or edited supplier takes a name the site already has
 *  (HTTP 409, `supplier-name-taken`, matched ignoring case and width). */
export class SupplierNameTakenError extends LogisticsApiError {
  constructor(failure: { serverMessage?: string; code?: string; details?: unknown } = {}) {
    super('a supplier with this name already exists', {
      serverMessage: '',
      ...failure,
      status: 409,
      reason: 'supplier-name-taken',
    });
    this.name = 'SupplierNameTakenError';
  }
}

/** An order write's supplier phone failed the server's check (HTTP 400,
 *  `supplier-phone-invalid`). The reply never carries the phone itself. */
export class SupplierPhoneInvalidError extends LogisticsApiError {
  constructor(failure: { serverMessage?: string; code?: string; details?: unknown } = {}) {
    super('the supplier phone is not a valid number', {
      serverMessage: '',
      ...failure,
      status: 400,
      reason: 'supplier-phone-invalid',
    });
    this.name = 'SupplierPhoneInvalidError';
  }
}

/** The typed error for an order write's refused supplier draft, or null when
 *  the refusal is about something else. */
export function supplierRefusalOf(failure: ILogisticsFailure): LogisticsApiError | null {
  if (failure.status === 409 && failure.reason === 'supplier-name-taken') return new SupplierNameTakenError(failure);
  if (failure.status === 400 && failure.reason === 'supplier-phone-invalid') return new SupplierPhoneInvalidError(failure);
  return null;
}

const suppliersBase = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}/suppliers`;

/** A refused supplier read as its typed error: 401, 404, else the plain failure. */
async function supplierFailure(res: Response, what: string): Promise<never> {
  if (res.status === 401) throw new UnauthorizedError();
  const failure = await readLogisticsFailure(res);
  if (res.status === 404) throw new LogisticsNotFoundError(failure);
  throw apiFailure(what, failure);
}

/**
 * A site's supplier list over an injected transport. Suppliers are written
 * only through an order's save (`newSupplier` / `supplierEdit` on the order
 * body, spec 2026-10-25 §12), so there is no write here.
 */
export function createSuppliersApi(authedFetch: AuthedFetch) {
  async function fetchSuppliers(siteId: string, signal?: AbortSignal): Promise<Supplier[]> {
    const res = await authedFetch(suppliersBase(siteId), { signal });
    if (res.ok) return ((await res.json()) as { suppliers: Supplier[] }).suppliers;
    return supplierFailure(res, 'fetch suppliers');
  }

  return { fetchSuppliers };
}
